import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface MaintenanceRecord {
  id?: number;
  recordNumber: string;
  maintenanceDate: Date;
  performedBy: number | null;

  // Truck Information
  truckId: number | null;
  truckNumber: string;
  mileage: number;
  hoursOfOperation: number;

  // Maintenance Type
  maintenanceType: "preventive" | "repair" | "inspection" | "emergency";
  workPerformed: string[];
  partsReplaced: {
    partName: string;
    partNumber: string;
    quantity: number;
    cost: number;
  }[];

  // Issues Found
  issuesFound: string;
  repairsNeeded: string;
  followUpRequired: boolean;
  followUpDate: Date | null;

  // Costs
  laborHours: number;
  laborRate: number;
  partsCost: number;
  totalCost: number;

  // Status
  truckStatus: "operational" | "limited_use" | "out_of_service";
  nextMaintenanceDate: Date | null;
  nextMaintenanceMileage: number | null;
  notes: string;
  status: "scheduled" | "in_progress" | "completed" | "cancelled";
}

class TruckMaintenanceWorkflowStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  maintenanceData: MaintenanceRecord;

  constructor(rootStore: RootStore) {
    this.maintenanceData = this.getDefaultMaintenance();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultMaintenance = (): MaintenanceRecord => ({
    recordNumber: this.generateRecordNumber(),
    maintenanceDate: new Date(),
    performedBy: null,

    truckId: null,
    truckNumber: "",
    mileage: 0,
    hoursOfOperation: 0,

    maintenanceType: "preventive",
    workPerformed: [],
    partsReplaced: [],

    issuesFound: "",
    repairsNeeded: "",
    followUpRequired: false,
    followUpDate: null,

    laborHours: 0,
    laborRate: 50,
    partsCost: 0,
    totalCost: 0,

    truckStatus: "operational",
    nextMaintenanceDate: null,
    nextMaintenanceMileage: null,
    notes: "",
    status: "scheduled",
  });

  generateRecordNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    return `TM${year}${month}${day}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateMaintenanceData = <K extends keyof MaintenanceRecord>(
    field: K,
    value: MaintenanceRecord[K]
  ) => {
    this.maintenanceData[field] = value;

    // Auto-calculate total cost
    if (field === "laborHours" || field === "laborRate" || field === "partsCost") {
      this.calculateTotalCost();
    }
  };

  calculateTotalCost = () => {
    const laborCost = this.maintenanceData.laborHours * this.maintenanceData.laborRate;
    this.maintenanceData.totalCost = laborCost + this.maintenanceData.partsCost;
  };

  validateCurrentStep = (): boolean => {
    this.setError(null);
    if (this.activeStep === 0 && !this.maintenanceData.truckId) {
      this.setError("Please select a truck");
      return false;
    }
    return true;
  };

  loadInitialData = async () => {
    this.setLoading(true);
    try {
      const trucksStore = this.rootStore.trucksStore as ServiceStore;
      await trucksStore?.find();
    } catch (err: any) {
      runInAction(() => this.setError("Failed to load data"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  submitMaintenance = async () => {
    this.setLoading(true);
    try {
      runInAction(() => {
        this.setSuccess("Maintenance record saved successfully!");
        this.activeStep = 3;
      });
    } catch (err: any) {
      runInAction(() => this.setError(err.message || "Failed to submit"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.maintenanceData = this.getDefaultMaintenance();
  };

  get trucks() {
    const trucksStore = this.rootStore.trucksStore as ServiceStore;
    return trucksStore?.entities || [];
  }
}

export default TruckMaintenanceWorkflowStore;
